import { useContext, useEffect } from 'react';

import {
  CLIENT_PREVIEW_PARAM,
  CLIENT_PREVIEW_STEPS_COMPLETED_PARAM,
  CLIENT_PREVIEW_STEPS_TOTAL_PARAM,
  CLIENT_PREVIEW_VALUE,
  STUDIO_CLIENT_PREVIEW_PARAM,
  STUDIO_CLIENT_PREVIEW_TASK_PROGRESS_PARAM,
} from 'src/constants/studioFtux';
import { RouteContext } from 'src/context';
import {
  getStudioClientPreviewProgressPct,
  STUDIO_CHECKLIST_COMPLETE_PCT,
  StudioChecklistTaskStatus,
} from 'src/features/appBuilder/getting-started-checklist/studioChecklist';
import history from 'src/history';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import {
  setClientPreviewSteps,
  setStudioClientPreview,
} from 'src/store/ui/actions';
import {
  selectClientPreviewSteps,
  selectIsStudioClientPreview,
  selectStudioClientPreviewProgress,
} from 'src/store/ui/selectors';
import type { ClientPreviewSteps } from 'src/store/ui/types';

export interface StudioClientPreview {
  /** True while this session is a client preview that shows "Getting started". */
  isPreview: boolean;
  /** Progress bar completion percentage (0-100) read off the preview link. */
  progress: number;
  /** The owner's current checklist, when the link carried it as steps. */
  steps: ClientPreviewSteps | null;
}

// Untrusted URL input: keep only whole counts where 0 <= completed <= total, so a
// tampered link can't draw an impossible card.
const parseSteps = (
  rawCompleted: string | undefined,
  rawTotal: string | undefined,
): ClientPreviewSteps | null => {
  const completed = Number(rawCompleted);
  const total = Number(rawTotal);
  if (
    !Number.isInteger(completed) ||
    !Number.isInteger(total) ||
    total < 1 ||
    completed < 0 ||
    completed > total
  ) {
    return null;
  }
  return { completed, total };
};

// Untrusted URL input: keep only a whole percentage within 0-100, so a tampered
// or malformed param can never over/under-fill the bar.
const parseProgress = (raw: string | undefined): number => {
  const parsed = Number(raw);
  if (!Number.isInteger(parsed)) {
    return 0;
  }
  return Math.min(Math.max(parsed, 0), STUDIO_CHECKLIST_COMPLETE_PCT);
};

// The activation email's link is minted at signup, before there is any progress
// to carry, so a studio landing without it starts where a fresh owner's preview
// would.
const FRESH_OWNER_PREVIEW_PCT = getStudioClientPreviewProgressPct({
  publishApp: StudioChecklistTaskStatus.Todo,
  explorePortal: StudioChecklistTaskStatus.Todo,
});

/**
 * Details of the current CRM "Open portal" client preview. The landing URL carries
 * the preview param for its workspace kind — `studioClientPreview` on Studio,
 * `clientPreview` on classic — plus, on Studio only, the "Getting started"
 * progress percentage.
 *
 * Both the preview flag and the progress percentage live in the (unpersisted)
 * store so every SidebarNav instance shares them — on mobile the temporary drawer
 * mounts a second one, and it survives that drawer remounting across route
 * transitions. Keeping progress local would let a later instance read `isPreview`
 * from the store while its own progress reset to 0 (the URL param is already
 * stripped), rendering an empty bar. Both params are stripped from the URL on
 * capture; a full refresh resets the store, so the preview ends there.
 */
export const useStudioClientPreview = (): StudioClientPreview => {
  const { query } = useContext(RouteContext);
  const dispatch = useAppDispatch();
  const isPreview = useAppSelector(selectIsStudioClientPreview);
  const progress = useAppSelector(selectStudioClientPreviewProgress);
  const steps = useAppSelector(selectClientPreviewSteps);

  useEffect(() => {
    const isPreviewLanding =
      query[CLIENT_PREVIEW_PARAM] === CLIENT_PREVIEW_VALUE ||
      query[STUDIO_CLIENT_PREVIEW_PARAM] === CLIENT_PREVIEW_VALUE;
    if (!isPreviewLanding) {
      return;
    }
    // Only the Studio param opens a "Getting started" preview. The plain one just
    // skips the sign-in interstitial: classic workspaces have no card, and Studio
    // sends it once the owner has already previewed (the card shows once).
    if (query[STUDIO_CLIENT_PREVIEW_PARAM] === CLIENT_PREVIEW_VALUE) {
      const rawProgress = query[STUDIO_CLIENT_PREVIEW_TASK_PROGRESS_PARAM];
      const landingProgress =
        rawProgress === undefined
          ? FRESH_OWNER_PREVIEW_PCT
          : parseProgress(rawProgress);
      dispatch(setStudioClientPreview(true, landingProgress));
      const landingSteps = parseSteps(
        query[CLIENT_PREVIEW_STEPS_COMPLETED_PARAM],
        query[CLIENT_PREVIEW_STEPS_TOTAL_PARAM],
      );
      if (landingSteps) {
        dispatch(setClientPreviewSteps(landingSteps));
      }
    }
    const params = new URLSearchParams(history.location.search);
    params.delete(CLIENT_PREVIEW_PARAM);
    params.delete(STUDIO_CLIENT_PREVIEW_PARAM);
    params.delete(STUDIO_CLIENT_PREVIEW_TASK_PROGRESS_PARAM);
    params.delete(CLIENT_PREVIEW_STEPS_COMPLETED_PARAM);
    params.delete(CLIENT_PREVIEW_STEPS_TOTAL_PARAM);
    history.replace({ search: params.toString() });
  }, [query, dispatch]);

  return { isPreview, progress, steps };
};
